import { useState } from 'react';
import { useTestConfigValue, useSetConfigValue } from '../hooks/useSystemConfig.js';
import Button from './Button.jsx';
import Spinner from './Spinner.jsx';

const FIELD_LABELS = {
  mp_platform_id:       'MP Platform ID',
  mp_user_id:           'MP User ID (plataforma)',
  external_webhook_url: 'Webhook URL global (fallback)',
};

export default function SystemConfigField({ configKey, description, hasValue, valuePreview, updatedAt }) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const testMutation = useTestConfigValue();
  const saveMutation = useSetConfigValue();

  const label = FIELD_LABELS[configKey] || configKey;

  const handleTest = async () => {
    setTestResult(null);
    try {
      const result = await testMutation.mutateAsync({ key: configKey, value: input });
      setTestResult(result);
      if (result.ok) setConfirmOpen(true);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.error || 'Error al testar' });
    }
  };

  const handleSave = async () => {
    try {
      await saveMutation.mutateAsync({ key: configKey, value: input });
      setEditing(false);
      setConfirmOpen(false);
      setInput('');
      setTestResult(null);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.error || 'Error al guardar' });
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setInput('');
    setTestResult(null);
    setConfirmOpen(false);
  };

  return (
    <div className="p-4 bg-bg-elevated rounded-lg border border-border space-y-2">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-200">{label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
        {!editing && (
          <Button variant="ghost" className="text-xs shrink-0" onClick={() => setEditing(true)}>
            ✏ Editar
          </Button>
        )}
      </div>

      {!editing && (
        <div className="flex items-center gap-2">
          {hasValue ? (
            <span className="font-mono text-xs text-slate-400 bg-bg rounded px-2 py-1">{valuePreview}</span>
          ) : (
            <span className="text-xs text-amber-400">⚠ No configurado</span>
          )}
          {updatedAt && (
            <span className="text-xs text-slate-600">
              · Actualizado {new Date(updatedAt).toLocaleDateString('es-AR')}
            </span>
          )}
        </div>
      )}

      {editing && !confirmOpen && (
        <div className="space-y-2">
          <input
            className="input-base text-sm font-mono"
            placeholder={`Nuevo valor para ${label}`}
            value={input}
            onChange={(e) => { setInput(e.target.value); setTestResult(null); }}
          />
          {testResult && (
            <div className={`text-xs px-3 py-2 rounded border ${
              testResult.ok
                ? 'bg-emerald-900/20 border-emerald-700/50 text-emerald-300'
                : 'bg-red-900/20 border-red-700/50 text-red-300'
            }`}>
              {testResult.ok ? '✓' : '✗'} {testResult.message}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" className="text-xs" onClick={handleCancel}>Cancelar</Button>
            <Button
              variant="secondary"
              className="text-xs"
              loading={testMutation.isPending}
              disabled={!input.trim()}
              onClick={handleTest}
            >
              ⚡ Testar valor
            </Button>
          </div>
        </div>
      )}

      {confirmOpen && (
        <div className="space-y-3 p-3 bg-amber-900/10 border border-amber-700/40 rounded-lg">
          <div className="flex items-start gap-2">
            <span className="text-amber-400 text-lg">⚠</span>
            <div>
              <p className="text-sm font-medium text-amber-300">Confirmar cambio</p>
              <p className="text-xs text-slate-400 mt-1">
                Este valor afecta todas las llamadas a MercadoPago. Un valor incorrecto puede interrumpir los cobros.
              </p>
            </div>
          </div>
          <div className="text-xs font-mono space-y-1">
            <div className="text-slate-400">Antes: <span className="text-slate-300">{valuePreview || 'vacío'}</span></div>
            <div className="text-emerald-400">Nuevo: <span className="text-slate-300">***{input.slice(-6)}</span></div>
          </div>
          {testResult && (
            <div className="text-xs px-2 py-1 rounded bg-emerald-900/20 border border-emerald-700/50 text-emerald-300">
              ✓ {testResult.message}
            </div>
          )}
          <div className="flex gap-2">
            <Button variant="ghost" className="text-xs" onClick={handleCancel}>Cancelar</Button>
            <Button variant="primary" className="text-xs" loading={saveMutation.isPending} onClick={handleSave}>
              ✓ Confirmar cambio
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
